import {useMemo} from 'react';

import {useRampFlagCoreweaveIssues} from '../../../state/issues/useCoreweaveIssues';
import {useRunsQueryContext} from '../../../state/runs/hooks';
import * as RunTypes from '../../../util/runTypes';

const KEY_NAME = {section: 'run', name: 'name'} as const;
const KEY_DISPLAY_NAME = {section: 'run', name: 'displayName'} as const;
const KEY_AGENT = {section: 'run', name: 'agent'} as const;
const KEY_STOPPED = {section: 'run', name: 'stopped'} as const;
const KEY_ISSUES = {section: 'run', name: 'issues'} as const;

// Keys that are shared among the three different batches of default keys used
// by different run selector instances.
const SHARED_KEYS: RunTypes.Key[] = [
  {section: 'run', name: 'state'},
  {section: 'run', name: 'notes'},
  {section: 'run', name: 'username'},
  {section: 'run', name: 'group'},
  {section: 'run', name: 'jobType'},
  {section: 'tags', name: '__ALL__'},
  {section: 'run', name: 'createdAt'},
  {section: 'run', name: 'updatedAt'},
  {section: 'run', name: 'heartbeatAt'},
  {section: 'run', name: 'duration'},
  {section: 'run', name: 'sweep'},
  {section: 'run', name: 'host'},
  {section: 'run', name: 'description'},
  {section: 'run', name: 'commit'},
  {section: 'run', name: 'github'},
  {section: 'run', name: 'runInfo.gpuCount'},
  {section: 'run', name: 'runInfo.gpu'},
];

function generateDefaultKeys(
  isSweep: boolean,
  isEarlyTerminate: boolean,
  canShowIssuesColumn: boolean
): RunTypes.Key[] {
  if (isSweep) {
    if (isEarlyTerminate) {
      // For early terminated sweeps?:
      return [
        KEY_NAME,
        KEY_DISPLAY_NAME,
        KEY_AGENT,
        KEY_STOPPED,
        ...SHARED_KEYS,
      ];
    }

    // For the sweep run selector:
    return [KEY_NAME, KEY_DISPLAY_NAME, KEY_AGENT, ...SHARED_KEYS];
  }

  // For the main run selector:
  return [
    KEY_NAME,
    KEY_DISPLAY_NAME,
    canShowIssuesColumn ? KEY_ISSUES : null,
    ...SHARED_KEYS,
  ].filter(Boolean) as RunTypes.Key[];
}

export function useDefaultKeys(): RunTypes.Key[] {
  const {sweep} = useRunsQueryContext();
  const canShowIssuesColumn = useRampFlagCoreweaveIssues();

  return useMemo(
    () =>
      generateDefaultKeys(
        Boolean(sweep),
        Boolean(sweep?.earlyTerminate),
        canShowIssuesColumn
      ),
    [sweep, canShowIssuesColumn]
  );
}
